"use client"

import { useState, useEffect, useMemo } from 'react'
import { useParams } from 'next/navigation'
import dynamic from 'next/dynamic'
import { ArrowLeft } from "lucide-react"
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import Link from 'next/link'
import ProjectDetails from '@/components/ProjectDetails'
import ProjectLocation from '@/components/ProjectLocation'
import ProjectFinancing from '@/components/ProjectFinancing'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { getStatusDisplayName } from '@/lib/types/listing'
import FillImage from '@/components/FillImage'

// Lazy load heavy components
const ProjectImageGallery = dynamic(() => import('@/components/ProjectImageGallery'), {
  loading: () => <div className="animate-pulse bg-gray-200 rounded-lg h-[400px]" />,
  ssr: false
})

const InvestmentBreakdown = dynamic(() => import('@/components/InvestmentBreakdown'), {
  loading: () => <div className="animate-pulse bg-gray-200 rounded-lg h-[300px]" />,
  ssr: false
})

const MortgageSimulator = dynamic(() => import('@/components/MortgageSimulator'), {
  loading: () => <div className="animate-pulse bg-gray-200 rounded-lg h-[400px]" />,
  ssr: false
})
import { 
  PaymentConfig, 
  FinancingOptions, 
  DisplayLabels, 
  ReserveConfig 
} from '@/types/paymentConfig'
import { CalculatedComponent } from '@/lib/utils/paymentCalculator'
import { generateMetaTitle } from '@/lib/utils/urlGenerator'

// Type definitions
type Image = {
  id: string
  url: string
  isPrimary: boolean
}

type Unit = {
  id: string
  unitNumber: string
  unitType: string
  floor: number
  bedrooms: number
  bathrooms: number
  totalSurface: number
  exteriorSurface?: number
  orientation: string
  status: string
  listPrice: number
}

type Listing = {
  id: string
  title: string
  description: string
  type: string
  status: string
  deliveryStatus: string
  location: string
  bedrooms: number
  bathrooms: number
  usableArea: number
  totalArea: number
  balconyArea: number | null
  orientation: string
  basePrice: number
  totalUnits: number
  availableUnits: number
  governmentBenefits: string | null
  financing: string | null
  parking: number
  storage: number
  features: string[]
  images: Image[]
  isLastUnits: boolean
  hasDownPaymentBonus: boolean
  developer: {
    id: string
    name: string
    slug: string
    logo: string | null
  }
  comuna?: string
  region?: string
  units?: Unit[]
  // Payment configuration
  paymentConfig?: PaymentConfig
  financingOptions?: FinancingOptions
  displayLabels?: DisplayLabels
  reserveConfig?: ReserveConfig
}

interface ProjectDetailClientProps {
  initialData?: Listing
}

export default function ProjectDetailClient({ initialData }: ProjectDetailClientProps) {
  const params = useParams()
  const slug = params?.slug as string
  const [listing, setListing] = useState<Listing | null>(initialData || null)
  const [isLoading, setIsLoading] = useState(!initialData)
  const [showSurvey, setShowSurvey] = useState(false)
  const [showStickyHeader, setShowStickyHeader] = useState(false)
  const [ufValue, setUfValue] = useState<number>(39228) // Default UF value
  const [userSelectedUnit, setUserSelectedUnit] = useState<Unit | null>(null)
  
  useEffect(() => {
    // Handle scroll to show/hide sticky header
    const handleScroll = () => {
      const titleElement = document.querySelector('[data-component-name="ProjectDetailPage"] h1');
      if (titleElement) {
        const titleRect = titleElement.getBoundingClientRect();
        // Show sticky header when title is scrolled out of view
        setShowStickyHeader(titleRect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Dynamic meta title for SEO
  useEffect(() => {
    if (listing && typeof document !== 'undefined') {
      document.title = generateMetaTitle(listing.title, listing.comuna)
      
      // Add structured data for SEO
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": listing.title,
        "description": listing.description || `Departamentos en venta en ${listing.comuna || 'Santiago'}`,
        "image": listing.images && listing.images.length > 0 ? listing.images[0].url : "",
        "offers": {
          "@type": "Offer",
          "price": listing.basePrice,
          "priceCurrency": "CLF",
          "availability": listing.availableUnits > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "seller": {
            "@type": "RealEstateAgent",
            "name": listing.developer?.name || "Obvio",
            "url": "https://obvio.cl"
          }
        },
        "address": {
          "@type": "PostalAddress",
          "addressLocality": listing.comuna || "Santiago",
          "addressCountry": "CL"
        },
        "additionalProperty": [
          {
            "@type": "PropertyValue",
            "name": "Dormitorios",
            "value": listing.bedrooms
          },
          {
            "@type": "PropertyValue", 
            "name": "Baños",
            "value": listing.bathrooms
          },
          {
            "@type": "PropertyValue",
            "name": "Superficie Total",
            "value": `${listing.totalArea} m²`
          }
        ]
      }
      
      // Remove existing structured data script if any
      const existingScript = document.querySelector('script[data-structured-data="project"]')
      if (existingScript) {
        existingScript.remove()
      }
      
      // Add new structured data script
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.setAttribute('data-structured-data', 'project')
      script.textContent = JSON.stringify(structuredData)
      document.head.appendChild(script)
    }
  }, [listing])

  // Fetch current UF value
  useEffect(() => {
    const fetchUFValue = async () => {
      try {
        const response = await fetch('/api/uf');
        if (response.ok) {
          const data = await response.json();
          if (data.data && data.data[0] && data.data[0].value) {
            setUfValue(data.data[0].value);
          }
        }
      } catch (error) {
        // Silently fall back to default UF value
      }
    };

    fetchUFValue();
  }, []);

  useEffect(() => {
    // Skip fetching if we already have initial data from server
    if (initialData) {
      return
    }
    
    let isMounted = true;
    
    const fetchProjectDetails = async () => {
      setIsLoading(true)
      try {
        // Try slug-based lookup first, fallback to ID for backward compatibility
        const response = await fetch(`/api/projects/public/by-slug/${slug}`)
        
        if (!response.ok) {
          throw new Error(`Failed to fetch project details: ${response.status}`)
        }
        
        const data = await response.json()
        
        // Only update state if component is still mounted
        if (isMounted) {
          if (data && data.id) {
            setListing(data)
          } else if (data && data.error) {
            throw new Error(data.error)
          } else {
            throw new Error('Invalid data format received')
          }
        }
      } catch (error) {
        // Silently handle errors - user will see "Project not found" message
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    if (slug && !initialData) {
      fetchProjectDetails()
    }
    
    // Cleanup function to prevent state updates after unmounting
    return () => {
      isMounted = false
    }
  }, [slug, initialData])

  const handlePreEvaluate = () => {
    // Track event
    if (typeof window !== 'undefined' && (window as any).dataLayer && listing) {
      (window as any).dataLayer.push({
        event: 'pre_evaluarme_click',
        listing_id: listing.id
      });
    }
    setShowSurvey(true)
  }

  const handleUnitSelection = (unit: any) => {
    setUserSelectedUnit(unit)
  }

  // Calculate the best unit price and data for the investment breakdown
  const selectedUnitData = useMemo(() => {
    if (!listing || !listing.units || listing.units.length === 0) {
      const currentPriceUF = listing?.basePrice || 0
      const currentPriceCLP = Math.round(currentPriceUF * ufValue)
      
      const unitPrice = {
        uf: Math.round(currentPriceUF),
        clp: currentPriceCLP
      }
      
      return {
        selectedUnit: null,
        unitPrice,
        originalPrice: null
      }
    }

    // Use user selected unit if available
    let selectedUnit = userSelectedUnit

    // If no user selection, fall back to automatic selection
    if (!selectedUnit) {
      const availableUnits = listing.units.filter(unit => 
        unit.status === 'available' && (
          unit.unitType?.toLowerCase() === 'departamento' || 
          unit.unitType?.toLowerCase() === 'apartment'
        )
      )

      if (availableUnits.length === 0) {
        const currentPriceUF = listing?.basePrice || 0
        const currentPriceCLP = Math.round(currentPriceUF * ufValue)
        
        const unitPrice = {
          uf: Math.round(currentPriceUF),
          clp: currentPriceCLP
        }
        
        return {
          selectedUnit: null,
          unitPrice,
          originalPrice: null
        }
      }

      selectedUnit = availableUnits.sort((a, b) => a.listPrice - b.listPrice)[0]
    }
    
    // The listPrice is stored in UF, so we need to convert to CLP
    const listPriceUF = selectedUnit.listPrice || listing?.basePrice || 0
    
    // IMPORTANT: Always pass the ORIGINAL price to InvestmentBreakdown
    // The InvestmentBreakdown component will handle ALL discount calculations internally
    // This prevents double discount application
    const originalPriceCLP = Math.round(listPriceUF * ufValue)
    
    const unitPrice = {
      uf: Math.round(listPriceUF),
      clp: originalPriceCLP
    }
    
    // Don't calculate discounts here - let InvestmentBreakdown handle it
    const originalPrice = null
    
    return {
      selectedUnit,
      unitPrice,
      originalPrice
    }
  }, [listing, userSelectedUnit, ufValue])

  // Determine what to show for "Comuna"
  const displayedComuna = listing?.comuna && listing.comuna.trim() !== '' 
    ? listing.comuna 
    : 'Santiago'

  // Determine what to show for location
  const displayedLocation = listing?.location && listing.location.trim() !== '' 
    ? listing.location 
    : 'Ubicación no especificada'

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-800"></div>
      </div>
    )
  }

  if (!listing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold mb-4">Proyecto no encontrado</h1>
        <Link href="/proyectos" className="text-green-800 hover:underline">
          Volver a proyectos
        </Link>
      </div>
    )
  }
  
  const { selectedUnit, unitPrice, originalPrice } = selectedUnitData

  return (
    <ErrorBoundary fallback={<div className="min-h-screen flex flex-col items-center justify-center">
      <h1 className="text-2xl font-bold mb-4">Algo salió mal</h1>
      <Link href="/proyectos" className="text-green-800 hover:underline">
        Volver a proyectos
      </Link>
    </div>}>
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Back button */}
      <div style={{ paddingTop: '24px' }}>
        <div className="container mx-auto px-3 sm:px-4 pb-4 sm:pb-6">
          <Link href="/proyectos" className="inline-flex items-center text-[#004132] hover:underline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a proyectos
          </Link>
        </div>
      </div>
      
      {/* Main content */}
      <div className="container mx-auto px-3 sm:px-4 pb-12 sm:pb-16">
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left column - Project Header, Details, and Location */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Project Header - Square Image and Info */}
            <div className="flex flex-col md:flex-row gap-4 sm:gap-6 p-4 sm:p-6" data-component-name="ProjectDetailPage">
              {/* Square Image */}
              <div className="relative w-full sm:w-36 md:w-44 h-36 sm:h-36 md:h-44 flex-shrink-0 rounded-lg overflow-hidden">
                {listing.images && listing.images.length > 0 && (
                  <FillImage
                    src={listing.images.find(img => img.isPrimary)?.url || listing.images[0].url}
                    alt={listing.title}
                    priority={true}
                  />
                )}
              </div>
              
              {/* Project Info */}
              <div className="flex flex-col justify-start items-start pt-2" data-component-name="ProjectDetailPage">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-thin text-[#004132] font-hector" style={{fontWeight: 200}} data-component-name="ProjectDetailPage">{listing?.title || 'Proyecto'}</h1>
                  <span className="font-semibold text-sm text-[#004132] bg-[#e6fa41] py-2 px-4 inline-block rounded-sm font-inter" data-component-name="ProjectDetails">
                    {getStatusDisplayName(listing.deliveryStatus || listing.status || '')}
                  </span>
                </div>
                {listing.developer && (
                  <div className="flex items-center mb-3">
                    <span className="text-gray-700 text-base sm:text-lg md:text-xl font-inter" data-component-name="ProjectDetailPage">{listing.developer.name}</span>
                  </div>
                )}
                <div className="flex items-center text-gray-600">
                  <span className="text-base sm:text-lg md:text-xl font-inter" data-component-name="ProjectDetailPage">
                    {displayedComuna}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Project Details */}
            <ProjectDetails 
              title={listing.title}
              description={listing.description}
              status={listing.status}
              deliveryStatus={listing.deliveryStatus}
              bedrooms={listing.bedrooms}
              bathrooms={listing.bathrooms}
              usableArea={listing.usableArea}
              totalArea={listing.totalArea}
              balconyArea={listing.balconyArea}
              orientation={listing.orientation}
              basePrice={listing.basePrice}
              totalUnits={listing.totalUnits}
              availableUnits={listing.availableUnits}
              units={listing.units}
              parking={listing.parking}
              storage={listing.storage}
              features={listing.features}
              paymentConfig={listing.paymentConfig}
              onUnitSelect={handleUnitSelection}
              projectImage={listing.images && listing.images.length > 0 ? (listing.images.find(img => img.isPrimary)?.url || listing.images[0].url) : undefined}
              location={displayedComuna}
              projectId={listing.id}
            />
            
            {/* Investment Breakdown */}
            <InvestmentBreakdown
              unitPrice={unitPrice}
              originalPrice={originalPrice}
              paymentConfig={listing.paymentConfig}
              financingOptions={listing.financingOptions}
              displayLabels={listing.displayLabels}
              reserveConfig={listing.reserveConfig}
              ufValue={ufValue}
              projectName={listing.title}
              unitNumber={selectedUnit?.unitNumber}
              unitBedrooms={selectedUnit?.bedrooms}
              unitTotalSurface={selectedUnit?.totalSurface}
              unitOrientation={selectedUnit?.orientation}
            />
            
            {/* Mortgage Simulator */}
            <MortgageSimulator
              loanAmountUF={(() => {
                // Calculate mortgage amount from payment config if available
                if (listing.paymentConfig && unitPrice) {
                  // Use payment calculator to get mortgage component
                  const { calculatePaymentBreakdown } = require('@/lib/utils/paymentCalculator');
                  const breakdown = calculatePaymentBreakdown(
                    unitPrice,
                    listing.paymentConfig,
                    listing.financingOptions,
                    listing.displayLabels,
                    listing.reserveConfig
                  );
                  const mortgageComponent = breakdown.components.find((c: CalculatedComponent) => c.type === 'credito_hipotecario');
                  return mortgageComponent?.amountUF || 0;
                }
                // Legacy fallback calculation
                const mortgageAmount = 76855223; // Default legacy value
                return mortgageAmount / ufValue;
              })()}
              loanAmountCLP={(() => {
                // Calculate mortgage amount from payment config if available
                if (listing.paymentConfig && unitPrice) {
                  const { calculatePaymentBreakdown } = require('@/lib/utils/paymentCalculator');
                  const breakdown = calculatePaymentBreakdown(
                    unitPrice,
                    listing.paymentConfig,
                    listing.financingOptions,
                    listing.displayLabels,
                    listing.reserveConfig
                  );
                  const mortgageComponent = breakdown.components.find((c: CalculatedComponent) => c.type === 'credito_hipotecario');
                  return mortgageComponent?.amountCLP || 0;
                }
                // Legacy fallback calculation
                return 76855223; // Default legacy value
              })()}
              mortgagePercentage={(() => {
                // Calculate mortgage percentage
                if (listing.paymentConfig && unitPrice) {
                  const { calculatePaymentBreakdown } = require('@/lib/utils/paymentCalculator');
                  const breakdown = calculatePaymentBreakdown(
                    unitPrice,
                    listing.paymentConfig,
                    listing.financingOptions,
                    listing.displayLabels,
                    listing.reserveConfig
                  );
                  const mortgageComponent = breakdown.components.find((c: CalculatedComponent) => c.type === 'credito_hipotecario');
                  return mortgageComponent?.percentage || Math.round((mortgageComponent?.amountCLP || 0) / unitPrice.clp * 100);
                }
                return 80; // Default percentage
              })()}
              ufValue={ufValue}
            />
            
            {/* Location */}
            <ProjectLocation 
              location={displayedLocation}
              comuna={displayedComuna}
            />
          </div>
          
          {/* Right column - Financing */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 sm:top-6">
              {/* Desktop sticky title - appears when scrolling past main title */}
              {showStickyHeader && (
                <div className="hidden lg:block bg-white p-4 rounded-lg shadow-sm mb-4">
                  <div className="flex items-start gap-4">
                    {/* Project image - responsive sizing */}
                    <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 rounded-md overflow-hidden">
                      {listing.images && listing.images.length > 0 && (
                        <FillImage
                          src={listing.images.find(img => img.isPrimary)?.url || listing.images[0].url}
                          alt={listing.title}
                        />
                      )}
                    </div>
                    
                    {/* Project info - further adjusted for balance with larger image */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <h2 className="text-xl sm:text-2xl font-thin text-[#004132] truncate font-hector" style={{fontWeight: 200}}>{listing?.title || 'Proyecto'}</h2>
                        <span className="font-semibold text-base text-[#004132] bg-[#e6fa41] py-1.5 px-4 inline-block rounded-sm font-inter" data-component-name="ProjectDetails">
                          {getStatusDisplayName(listing.deliveryStatus || listing.status || '')}
                        </span>
                      </div>
                      <div className="text-base font-inter text-gray-600 truncate">
                        {displayedComuna}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              
              {/* Financing Component */}
              <ProjectFinancing 
                basePrice={listing.basePrice}
                governmentBenefits={listing.governmentBenefits}
                financing={listing.financing}
                hasDownPaymentBonus={listing.hasDownPaymentBonus}
                onPreEvaluate={handlePreEvaluate}
                unitNumber={selectedUnit?.unitNumber}
                unitBedrooms={selectedUnit?.bedrooms}
                unitTotalSurface={selectedUnit?.totalSurface}
                unitOrientation={selectedUnit?.orientation}
                unitFloor={selectedUnit?.floor}
                unitPrice={unitPrice}
                paymentConfig={listing.paymentConfig}
                projectTitle={listing.title}
                projectId={listing.id}
                unitId={selectedUnit?.id}
              />
            </div>
          </div>
        </div>
      </div>
      
      <Footer fetchFresh={true} />
      
      {/* Improved Survey modal with proper focus management */}
      {showSurvey && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            // Close when clicking outside the modal
            if (e.target === e.currentTarget) {
              setShowSurvey(false);
            }
          }}
        >
          <div 
            className="bg-white p-6 rounded-lg max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Pre-evaluación</h2>
              <button
                onClick={() => setShowSurvey(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
            <p className="mb-4">Implementación del formulario de pre-evaluación aquí.</p>
            <button 
              onClick={() => setShowSurvey(false)}
              className="bg-[#004132] text-white px-4 py-2 rounded-md"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
    </ErrorBoundary>
  )
}